import { getField, updateField } from 'vuex-map-fields'
import { i18n } from '@/translations/i18n'
import { trackScreenView } from '../../helper'

const fallbackRequiredFields = [
  'agb',
  'email',
  'gender',
  'firstname',
  'lastname',
]

const state = {
  cart: null,
  quickCartVisible: false,
  isGuestUserRequested: false,
  beautyDealsSuccess: null,
}

function getCartItems(cart) {
  if (!cart) return []
  return cart.cart_items ?? cart.cart?.cart_items ?? []
}

const quantityDebounce = {}
let getCartDebounceTimer = null
const DEBOUNCE_MS = 250
const GET_CART_DEBOUNCE_MS = 600

const getters = {
  isPending: ({ pending }) => pending,
  getField,
  isGuestUserRequested: (state) => state.isGuestUserRequested,
}

const actions = {
  async createPaypalPayment({ commit }, { payload, paylater = false }) {
    commit('SET_PENDING', true)
    try {
      const target = paylater ? 'checkout.paypal_complete?paylater=true' : 'checkout.paypal_complete'
      const { data } = await this.api.items.createItem(target, payload)
      if (data?.url) {
        window.location.href = data.url
      }
    } catch (err) {
      commit('SET_ERROR', true)
      throw err
    } finally {
      commit('SET_PENDING', false)
    }
  },
  async getCart({ commit }, payload = {}) {
    if (!payload.silent) commit('SET_PENDING', true)
    try {
      const { data } = await this.api.items.getItem('cart')
      commit('SET_CART', data)
      commit('EMIT_CART_UPDATED', data)
    } finally {
      if (!payload.silent) commit('SET_PENDING', false)
    }
  },
  async getSummary({ commit }, payload) {
    commit('SET_PENDING', true)
    try {
      await this.api.items.createItem('cart.summary', { comment: payload })
      commit('SET_COMMENT', payload)
    } finally {
      commit('SET_PENDING', false)
    }
  },
  updateCartItemOption({ commit, dispatch, state }, { id, option }) {
    const isQty = option.amount != null
    const items = getCartItems(state.cart)
    const item = items.find((i) => i.id === id)
    const prevQty = item ? (item.quantity ?? item.amount) : null

    if (isQty && prevQty != null) {
      commit('UPDATE_CART_ITEM_QUANTITY', { id, amount: option.amount })
    }

    if (isQty) {
      return new Promise((resolve, reject) => {
        const pending = quantityDebounce[id]
        if (pending) {
          clearTimeout(pending.timer)
          pending.resolve()
        }
        const timer = setTimeout(async () => {
          delete quantityDebounce[id]
          try {
            const it = getCartItems(this.state.cart).find((i) => i.id === id)
            const amount = it?.quantity ?? it?.amount ?? option.amount
            const { data } = await this.api.items.editItem(`cart.products.${id}`, { amount })

            if (data?.tracking_data) {
              trackScreenView('ChangeQty', { tracking: data.tracking_data, type: 'event' })
            }
            if (getCartDebounceTimer) clearTimeout(getCartDebounceTimer)
            getCartDebounceTimer = setTimeout(() => {
              getCartDebounceTimer = null
              dispatch('getCart', { silent: true })
            }, GET_CART_DEBOUNCE_MS)
            resolve(data)
          } catch (err) {
            if (prevQty != null) commit('UPDATE_CART_ITEM_QUANTITY', { id, amount: prevQty })
            dispatch('getCart', { silent: true })
            reject(err)
          }
        }, DEBOUNCE_MS)
        quantityDebounce[id] = { timer, resolve, reject }
      })
    }

    return this.api.items.editItem(`cart.products.${id}`, option).then(({ data }) => {
      dispatch('getCart', { silent: true })
      return data
    })
  },
  async removeItem({ commit, dispatch }, id) {
    commit('SET_PENDING', true)
    try {
      const { data } = await this.api.items.deleteItem(`cart.products.${id}`)
      if (data?.tracking_data) {
        trackScreenView('RemoveFromCart', { tracking: data.tracking_data, type: 'event' })
      }
      await dispatch('getCart')
    } finally {
      commit('SET_PENDING', false)
    }
  },
  async removeProduct({ commit }, productId) {
    if (!productId) return
    commit('SET_PENDING', true)
    try {
      const { data } = await this.api.items.deleteItem(`cart.products.${productId}`)
      if (data?.tracking_data) {
        trackScreenView('RemoveFromCart', { tracking: data.tracking_data, type: 'event' })
      }
    } finally {
      commit('SET_PENDING', false)
    }
  },
  async removeAllItems({ commit, dispatch }) {
    commit('SET_PENDING', true)
    try {
      const { data } = await this.api.items.deleteItem(`cart.items`)
      if (data?.tracking_data) {
        trackScreenView('RemoveFromCart', { tracking: data.tracking_data, type: 'event' })
      }
      await dispatch('getCart')
    } finally {
      commit('SET_PENDING', false)
    }
  },
  async updateComment({ commit }, payload) {
    commit('SET_PENDING', true)
    try {
      await this.api.items.createItem('carts.mine.comment', { comment: payload })
      commit('SET_COMMENT', payload)
    } finally {
      commit('SET_PENDING', false)
    }
  },
  async purchase({ state, commit }, newsletter) {
    commit('SET_ERROR', false)
    if (state.min_order_value.reached && !state.pendingPurchase) {
      commit('SET_PENDING_PURCHASE', true)
      try {
        const { data } = await this.api.items.createItem('payments', {
          agb: state.agb,
          newsletter,
          create_account: state.create_account
        })
        if (data.result && data.redirect) {
          window.location.href = data.redirect
        } else if (data.redirect === '' || data.redirect === null) {
          commit('SET_PAYMENT_ERROR', true)
        } else {
          commit('SET_ERROR', true)
        }
      } catch (err) {
        commit('SET_ERROR', true)
        throw err
      } finally {
        commit('SET_PENDING_PURCHASE', false)
      }
    } else {
      commit('SET_ERROR', true)
    }
  },
  openQuickCart({ commit }) {
    commit('OPEN_QUICKCART')
  },
  closeQuickCart({ commit }) {
    commit('CLOSE_QUICKCART')
  },
  async addBeautyDeal({ commit }, beautyDealIds) {
    if (!beautyDealIds?.length) return
    commit('REMOVE_BEAUTY_DEALS_SUCCESS')
    const { data } = await this.api.items.createItem('cart.beauty-deals', { beauty_deal_ids: beautyDealIds })
    const msg = i18n.global?.t?.('app.free_samples.title') || 'Free Samples Added'
    commit('SET_BEAUTY_DEALS_SUCCESS', { msg })
    return data
  },
  removeBeautyDeal(_, idOrIds) {
    if (idOrIds == null) return
    if (Array.isArray(idOrIds)) {
      if (!idOrIds.length) return
      return this.api.items.deleteItem('cart.beauty-deals', {}, { ids: idOrIds })
    }
    return this.api.items.deleteItem(`cart.beauty-deals.${idOrIds}`)
  },
  async removeWishlistProduct({ commit }, { productId, wishlistId }) {
    if (!productId) return
    commit('SET_PENDING', true)
    try {
      await this.api.items.deleteItem(`cart.products.${productId}`, {}, { wishlist_id: wishlistId })
    } finally {
      commit('SET_PENDING', false)
    }
  },
}

const mutations = {
  SET_PENDING: (state, payload) => {
    state.pending = payload
  },
  OPEN_QUICKCART: state => {
    state.quickCartVisible = true
  },
  CLOSE_QUICKCART: state => {
    state.quickCartVisible = false
  },
  SET_CART: (state, payload) => {
    state.cart = payload
  },
  EMIT_CART_UPDATED: (state, payload) => {
    try {
      const event = new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: payload },
      })
      if (typeof window !== 'undefined' && window.dispatchEvent) window.dispatchEvent(event)
      if (typeof document !== 'undefined' && document.dispatchEvent) document.dispatchEvent(event)
    } catch (e) {
      console.error('Failed to emit cart:updated event', e)
    }
  },
  SET_GUEST_USER_REQUESTED: (state, payload) => {
    state.isGuestUserRequested = Boolean(payload)
  },
  UPDATE_CART_ITEM_QUANTITY: (state, { id, amount }) => {
    const items = getCartItems(state.cart)
    const item = items.find((i) => i.id === id)
    if (item) {
      item.quantity = amount
      if (typeof item.amount === 'number') item.amount = amount
    }
  },
  SET_BEAUTY_DEALS_SUCCESS: (state, payload) => {
    state.beautyDealsSuccess = payload
  },
  REMOVE_BEAUTY_DEALS_SUCCESS: (state) => {
    state.beautyDealsSuccess = null
  },
  updateField,
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
